export { decorateUrl, DecorateUrlInput };

import {
  buildProtoCtx,
  PROTOCTX_PARAM,
  PROTOCTX_VIA_KEY,
  PROTOCTX_VID_KEY,
  UTMValues,
} from "./Tracking";

// Propagación de tracking entre páginas del funnel (serie protoctx v2,
// 5/8/2026). Hasta ahora NINGUNA navegación interna conservaba la query: las
// URLs de siguiente página se hornean absolutas y peladas, y los usuarios sin
// storage perdían atribución e identidad en cada salto (visitor id nuevo por
// página). decorateUrl construye la URL destino con:
//
//   1. PASSTHROUGH de la query actual: lo que viaja en la URL sigue viajando
//      (gclid/fbclid/tblci... EN CLARO — los píxeles de las páginas
//      siguientes los leen; p.ej. el de Meta reconstruye _fbc desde fbclid).
//      Exclusiones deliberadas (comparadas SIN case — los lectores
//      lowercasean la query, así que un `PROTOCTX=` manual también cuenta):
//        - protoctx: se REGENERA fresco desde estado (nunca se arrastra uno
//          viejo — podría ser de otra sesión u otro formato)
//        - proto_c y __pfr_force_*: FunnelParams los persiste al cargar (storage
//          del mismo origen) y el funnel-router los lee de ahí; arrastrarlos por
//          la URL los duplicaría. En un salto a otro dominio no viajan (aceptado)
//        - valores con pinta de email (pre-fill de campañas de correo):
//          replicarlos por todo el funnel los metería en web_event.url y en
//          los page_location de los píxeles — PII que debe morir en la
//          landing (revisión 6/8/2026)
//   2. protoctx v2 generado desde ESTADO (no desde la URL, que tras la
//      landing ya no lleva utm_*): 6 UTMs efectivas + `vid:<visitor id>` +
//      `via:<procedencia>` (para que el marcador pctx viaje sin falsos
//      positivos). El `vid` SOLO se incluye si el destino es del MISMO host:
//      la identidad es first-party y no se filtra a dominios ajenos aunque
//      el editor marque un link externo (revisión 6/8/2026).
//   3. Los params PROPIOS de la URL destino ganan sobre el passthrough
//      (si el editor puso algo explícito en el link, se respeta) — excepto
//      protoctx, que siempre es el fresco. Los params repetidos del origen
//      (?tag=a&tag=b) se conservan todos.
//
// Contrato de resiliencia: NUNCA lanza; ante cualquier problema (URL rota,
// esquema no-http, entorno degenerado) devuelve la URL original intacta.
// Idempotente: decorar dos veces produce el mismo resultado (necesario
// porque el listener global puede reescribir el mismo href en clicks
// repetidos).

interface DecorateUrlInput {
  // La query actual de la página (window.location.search)
  currentSearch: string;
  // UTMs efectivas (Vault con fallback a memoria — tracking.getAdditionalDataToMessage())
  utm: UTMValues;
  // Visitor id efectivo ("" si por lo que sea no hay — se omite la extensión)
  visitorId: string;
  // Procedencia de la atribución vigente ("pctx", "whop" o "") —
  // tracking.attributionVia. Viaja como `via:` y la página siguiente la
  // conserva (Tracking, FORWARDED_VIA_MARKERS)
  attributionVia?: string;
}

const EXCLUDED_PASSTHROUGH_PREFIX = "__pfr_force_";
const EXCLUDED_PASSTHROUGH = [PROTOCTX_PARAM, "proto_c"];
const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function decorateUrl(targetUrl: string, input: DecorateUrlInput): string {
  try {
    if (typeof targetUrl !== "string" || targetUrl.trim() === "") {
      return targetUrl;
    }

    const base =
      typeof window !== "undefined" && window.location
        ? window.location.href
        : undefined;
    const target = new URL(targetUrl, base);

    if (target.protocol !== "http:" && target.protocol !== "https:") {
      return targetUrl;
    }

    // Los params explícitos del destino ganan; el protoctx del destino no
    // cuenta (siempre se regenera — también si viene con otro case)
    const finalParams = new URLSearchParams(target.search);
    for (const name of Array.from(finalParams.keys())) {
      if (name.toLowerCase() === PROTOCTX_PARAM) {
        finalParams.delete(name);
      }
    }
    const targetOwnNames = new Set(
      Array.from(finalParams.keys()).map((name) => name.toLowerCase()),
    );

    // Passthrough de la query actual, con las exclusiones (sin case)
    const carried = new URLSearchParams(input.currentSearch || "");
    carried.forEach((value, name) => {
      const lowered = name.toLowerCase();
      if (EXCLUDED_PASSTHROUGH.indexOf(lowered) !== -1) {
        return;
      }
      if (lowered.indexOf(EXCLUDED_PASSTHROUGH_PREFIX) === 0) {
        return;
      }
      if (EMAIL_LIKE.test(value.trim())) {
        return;
      }
      if (targetOwnNames.has(lowered)) {
        return;
      }
      finalParams.append(name, value);
    });

    // protoctx SIEMPRE fresco desde estado. El vid viaja TAMBIÉN entre hosts
    // (decisión 6/8/2026): los funnels pueden repartir páginas entre dominios
    // distintos, y ahí la URL es el ÚNICO canal de identidad para todo el
    // mundo — el storage es por-origen incluso en navegadores sanos. Riesgo
    // asumido: un link EXTERNO marcado a mano lleva el vid a logs de terceros
    // — es opt-in explícito del editor, el id es pseudónimo y solo significa
    // algo contra nuestro visitor-store, y la adopción en destino sigue
    // acotada por formato uuidv7 + frescura 24h.
    const extensions: Record<string, string> = {};
    if (typeof input.visitorId === "string" && input.visitorId !== "") {
      extensions[PROTOCTX_VID_KEY] = input.visitorId;
    }
    // `via` viaja SIEMPRE en lo generado: es el sello de "esto es forwarding,
    // no un ctx de buyer" — sin él, un salto cross-host (sin vid) de tráfico
    // de pago limpio se confundiría con un rescate y marcaría pctx en falso.
    // "fwd" = procedencia neutra (los UTMs vinieron de utm_* o storage).
    extensions[PROTOCTX_VIA_KEY] =
      typeof input.attributionVia === "string" && input.attributionVia !== ""
        ? input.attributionVia
        : "fwd";
    const protoCtx = buildProtoCtx(input.utm, extensions);
    if (protoCtx !== "") {
      finalParams.set(PROTOCTX_PARAM, protoCtx);
    }

    target.search = finalParams.toString();
    return target.toString();
  } catch {
    // jamás romper una navegación por decorarla
    return targetUrl;
  }
}
